import React, { forwardRef, useRef, useState } from 'react'
import mergeRefs from 'react-merge-refs'

import { ClearButton } from 'src/shared/components/СlearButton'
import { Controller } from '../Controller'

import type { CommonInputProps } from './typing'
import styles from './index.module.css'

export const Input = forwardRef<HTMLInputElement, CommonInputProps>(
  (
    {
      value,
      defaultValue,
      clear = false,
      className,
      fieldClassName,
      labelClassName,
      disabled,
      readOnly = false,
      label,
      labelClearButton,
      type = 'text',
      hint,
      error,
      block = true,
      leftAddons,
      leftAddonsProps,
      rightAddons,
      rightAddonsProps,
      onChange,
      onFocus,
      onBlur,
      onClear,
      onClick,
      onAnimationStart,
      ...restProps
    },
    ref,
  ) => {
    const uncontrolled = value === undefined
    const inputRef = useRef<HTMLInputElement>(null)

    const [stateValue, setStateValue] = useState<string>(defaultValue || '')

    const filled = Boolean(uncontrolled ? stateValue : value)
    const [autofilled, setAutofilled] = useState<boolean>(false)
    const [focused, setFocused] = useState<boolean>(false)

    const clearButtonVisible = clear && filled && !disabled

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      let inputValue = event.target.value
      const target = event.target as HTMLInputElement
      const isInputTypeNumber = target.getAttribute('type') === 'number'
      const pattern = /[eE]/g

      if (isInputTypeNumber && pattern.test(inputValue)) {
        inputValue = inputValue.replace(pattern, '')
      }

      if (onChange) {
        onChange(event, { value: inputValue })
      }

      if (uncontrolled) {
        setStateValue(inputValue)
      }
    }

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      if (!readOnly) {
        setFocused(true)
      }

      if (onFocus) {
        onFocus(event)
      }
    }

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      setFocused(false)

      if (onBlur) {
        onBlur(event)
      }
    }

    const handleAnimationStart = (event: React.AnimationEvent<HTMLInputElement>) => {
      if (onAnimationStart) {
        onAnimationStart(event)
      }

      setAutofilled(event.animationName.includes('start'))
    }

    const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!clearButtonVisible) return

      if (uncontrolled) {
        setStateValue('')
      }

      if (onClear) {
        onClear(event)
      }

      if (inputRef.current) {
        inputRef.current.focus()
      }
    }

    const renderRightAddons = () => {
      const rightAddonsVisible = clearButtonVisible || rightAddons

      return (
        rightAddonsVisible && (
          <>
            {clearButtonVisible && (
              <ClearButton className={styles.clearIcon} label={labelClearButton} onClick={handleClear} />
            )}
            {rightAddons}
          </>
        )
      )
    }

    return (
      <Controller
        className={className}
        fieldClassName={fieldClassName}
        labelClassName={labelClassName}
        hint={hint}
        error={error}
        filled={filled || autofilled || focused}
        focused={focused}
        disabled={disabled}
        readOnly={readOnly}
        block={block}
        label={label}
        leftAddons={leftAddons}
        leftAddonsProps={leftAddonsProps}
        rightAddons={renderRightAddons()}
        rightAddonsProps={rightAddonsProps}
      >
        <input
          ref={mergeRefs([ref, inputRef])}
          type={type}
          value={uncontrolled ? stateValue : value}
          readOnly={readOnly}
          disabled={disabled}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onAnimationStart={handleAnimationStart}
          className={styles.input}
          aria-label={typeof label === 'string' ? label : undefined}
          {...restProps}
        />
      </Controller>
    )
  },
)

Input.displayName = 'Input'
